import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import * as authApi from "../api/auth";
import { definirContextoRpc } from "../api/client";
import type { UsuarioSessao } from "../types/auth";
import { useGlobalLoader } from "../components/ui/GlobalLoader";
import { useIdioma } from "../i18n/IdiomaProvider";
import { guardarMensagemSessao } from "./mensagemSessao";
import { monitorarInatividadeSessao, registrarInicioSessao } from "./inatividadeSessao";
import { carregarConfiguracoesSistemaPublicas, notificarAssetsSistemaAtualizados, sincronizarConfiguracoesSistemaPublicas, useConfiguracoesSistemaPublicas } from "../utils/configuracoesSistema";
import { definirEmpresaAtivaPadrao } from "../utils/empresaPadrao";
import { sincronizarFavoritosDoLogin } from "../utils/favoritosLocal";

interface AuthContextValue {
  usuario: UsuarioSessao | null;
  carregando: boolean;
  entrar: (email: string, senha: string, turnstileToken?: string, codEmpresa?: string) => Promise<UsuarioSessao>;
  entrarComGoogle: (accessToken: string, codEmpresa?: string) => Promise<UsuarioSessao>;
  confirmarDoisFatores: (codigo: string) => Promise<UsuarioSessao>;
  usarSessaoAqui: () => Promise<UsuarioSessao>;
  sair: () => Promise<void>;
  recarregarSessao: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<UsuarioSessao | null>(null);
  const [carregando, setCarregando] = useState(true);
  const { showLoading, hideLoading } = useGlobalLoader();
  const { t } = useIdioma();
  const configuracoes = useConfiguracoesSistemaPublicas();
  const inatividadeHabilitadaRef = useRef(configuracoes.logoutInatividade);
  const minutosInatividadeRef = useRef(configuracoes.logoutInatividadeMinutos);
  const tinhaSessaoRef = useRef(false);
  const encerrandoRef = useRef(false);
  const revisaoSessaoRef = useRef(0);

  const aplicarSessao = useCallback(async (sessao: UsuarioSessao | null, novoLogin = false) => {
    if (novoLogin && sessao) {
      revisaoSessaoRef.current += 1;
      encerrandoRef.current = false;
      registrarInicioSessao(sessao.codLogin);
    }
    const revisao = revisaoSessaoRef.current;
    definirContextoRpc(sessao);
    try {
      // Carrega as APIs da empresa autenticada antes de liberar as páginas.
      if (novoLogin && sessao) await carregarConfiguracoesSistemaPublicas();
      await sincronizarFavoritosDoLogin(sessao?.codLogin);
    } catch {
      // A sessão não é bloqueada se o cache de favoritos falhar.
    }
    // Uma resposta pendente não pode restaurar o usuário depois do logout.
    if (revisao !== revisaoSessaoRef.current || (sessao && encerrandoRef.current)) return;
    setUsuario(sessao);
  }, []);

  /* Fonte única da empresa inicial dos modais: acompanha login, troca de
     empresa no seletor superior, recarga da sessão e logout. */
  useEffect(() => {
    definirEmpresaAtivaPadrao(usuario?.codEmpresa);
  }, [usuario?.codEmpresa]);

  /**
   * Valida a sessão no servidor. Quando o back-end derruba a sessão por conta
   * própria (login desativado no cadastro, por exemplo) ele devolve o motivo:
   * guardamos o recado para a tela de login e, se o usuário estava dentro do
   * sistema, forçamos a volta para /login com um reload real.
   */
  const sincronizarSessao = useCallback(async (atualizarAssets = true) => {
    const revisao = revisaoSessaoRef.current;
    const resultado = await authApi.obterSessaoDetalhada();
    if (revisao !== revisaoSessaoRef.current || encerrandoRef.current || resultado.indisponivel) return;
    if (resultado.configuracoesSessao) sincronizarConfiguracoesSistemaPublicas(resultado.configuracoesSessao);
    if (atualizarAssets) await aplicarSessao(resultado.usuario);
    // A checagem de segurança só encerra sessões inválidas. Atualizar o perfil
    // aqui reiniciaria filtros e consultas das páginas a cada verificação.
    else if (!resultado.usuario) { definirContextoRpc(null); setUsuario(null); }
    if (revisao !== revisaoSessaoRef.current || encerrandoRef.current) return;
    if (resultado.usuario) {
      tinhaSessaoRef.current = true;
      if (atualizarAssets) notificarAssetsSistemaAtualizados();
      return;
    }
    if (!resultado.mensagem) return;
    guardarMensagemSessao(resultado.mensagem);
    if (tinhaSessaoRef.current && !encerrandoRef.current) {
      encerrandoRef.current = true;
      window.location.assign("/login");
    }
  }, [aplicarSessao]);

  const recarregarSessao = useCallback(async () => {
    await sincronizarSessao();
  }, [sincronizarSessao]);

  useEffect(() => {
    let ativo = true;
    showLoading(t("loadingText"));
    (async () => {
      try {
        await sincronizarSessao();
        if (ativo) setCarregando(false);
      } finally {
        if (ativo) hideLoading();
      }
    })();
    return () => {
      ativo = false;
    };
  }, [hideLoading, showLoading, sincronizarSessao, t]);

  const entrar = useCallback(
    async (email: string, senha: string, turnstileToken = "", codEmpresa = "") => {
      showLoading(t("loadingText"));
      try {
        const sessao = await authApi.login(email, senha, turnstileToken, codEmpresa);
        await aplicarSessao(sessao, true);
        tinhaSessaoRef.current = true;
        notificarAssetsSistemaAtualizados();
        return sessao;
      } finally {
        hideLoading();
      }
    },
    [aplicarSessao, hideLoading, showLoading, t]
  );

  const entrarComGoogle = useCallback(
    async (accessToken: string, codEmpresa = "") => {
      showLoading(t("loadingText"));
      try {
        const sessao = await authApi.loginComGoogle(accessToken, codEmpresa);
        await aplicarSessao(sessao, true);
        tinhaSessaoRef.current = true;
        notificarAssetsSistemaAtualizados();
        return sessao;
      } finally {
        hideLoading();
      }
    },
    [aplicarSessao, hideLoading, showLoading, t]
  );

  const confirmarDoisFatores = useCallback(
    async (codigo: string) => {
      showLoading(t("loadingText"));
      try {
        const sessao = await authApi.verificarCodigo2FA(codigo);
        await aplicarSessao(sessao, true);
        tinhaSessaoRef.current = true;
        notificarAssetsSistemaAtualizados();
        return sessao;
      } finally {
        hideLoading();
      }
    },
    [aplicarSessao, hideLoading, showLoading, t]
  );

  const usarSessaoAqui = useCallback(async () => {
    showLoading(t("loadingText"));
    try {
      const sessao = await authApi.confirmarSessaoAqui();
      await aplicarSessao(sessao, true);
      tinhaSessaoRef.current = true;
      notificarAssetsSistemaAtualizados();
      return sessao;
    } finally {
      hideLoading();
    }
  }, [aplicarSessao, hideLoading, showLoading, t]);

  const sair = useCallback(
    async () => {
      revisaoSessaoRef.current += 1;
      encerrandoRef.current = true;
      definirContextoRpc(null);
      setUsuario(null);
      tinhaSessaoRef.current = false;
      showLoading(t("loadingText"));
      try {
        await authApi.logout();
      } finally {
        notificarAssetsSistemaAtualizados();
        hideLoading();
      }
    },
    [hideLoading, showLoading, t]
  );

  const codLoginAtivo = usuario?.codLogin;
  useEffect(() => {
    if (codLoginAtivo === undefined) return;
    if (!configuracoes.logoutInatividade) {
      inatividadeHabilitadaRef.current = false;
      registrarInicioSessao(codLoginAtivo);
      return;
    }
    // Reativar ou alterar o tempo concede um novo prazo; recargas preservam o existente.
    if (!inatividadeHabilitadaRef.current || minutosInatividadeRef.current !== configuracoes.logoutInatividadeMinutos) {
      registrarInicioSessao(codLoginAtivo);
    }
    inatividadeHabilitadaRef.current = true;
    minutosInatividadeRef.current = configuracoes.logoutInatividadeMinutos;
    let ativo = true;
    const pararMonitoramento = monitorarInatividadeSessao(codLoginAtivo, () => {
      if (encerrandoRef.current) return;
      const revisao = revisaoSessaoRef.current;
      void (async () => {
        // Uma política alterada em outra máquina também vale para abas suspensas.
        const resultado = await authApi.obterSessaoDetalhada();
        if (!ativo || encerrandoRef.current || revisao !== revisaoSessaoRef.current) return;
        const politica = resultado.configuracoesSessao;
        if (resultado.usuario && politica && (!politica.logoutInatividade || politica.logoutInatividadeMinutos !== configuracoes.logoutInatividadeMinutos)) {
          registrarInicioSessao(codLoginAtivo);
          sincronizarConfiguracoesSistemaPublicas(politica);
          return;
        }
        const tempo = configuracoes.logoutInatividadeMinutos === 1 ? "1 minuto" : `${configuracoes.logoutInatividadeMinutos} minutos`;
        guardarMensagemSessao(resultado.mensagem || `Sessão encerrada após ${tempo} sem atividade. Faça login novamente.`);
        // Reutiliza o logout do servidor e a navegação das rotas protegidas.
        await sair();
      })();
    }, configuracoes.logoutInatividadeMinutos);
    return () => {
      ativo = false;
      pararMonitoramento();
    };
  }, [codLoginAtivo, configuracoes.logoutInatividade, configuracoes.logoutInatividadeMinutos, sair]);

  useEffect(() => {
    if (codLoginAtivo === undefined) return;
    let consultando = false;
    async function verificarSessao() {
      if (document.visibilityState !== "visible" || consultando || encerrandoRef.current) return;
      consultando = true;
      try {
        await sincronizarSessao(false);
      } finally {
        consultando = false;
      }
    }
    // O servidor revoga imediatamente; a interface acompanha sem depender de uma ação do usuário.
    const timer = window.setInterval(() => void verificarSessao(), 15_000);
    window.addEventListener("focus", verificarSessao);
    document.addEventListener("visibilitychange", verificarSessao);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", verificarSessao);
      document.removeEventListener("visibilitychange", verificarSessao);
    };
  }, [codLoginAtivo, sincronizarSessao]);

  return (
    <AuthContext.Provider
      value={{ usuario, carregando, entrar, entrarComGoogle, confirmarDoisFatores, usarSessaoAqui, sair, recarregarSessao }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth deve ser usado dentro de <AuthProvider>.");
  return ctx;
}
